import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { buildKarlAsks } from "../karlAsks";
import { askAnswerEffect } from "./askAnswerEffect";
import { askArmSettled } from "./askArmSettled";
import { karlAskQueueReducer } from "./karlAskQueueReducer";
import { isSamePlanSnapshot, planSnapshot } from "./planSnapshot";
import { readAskQueue } from "./readAskQueue";
import {
  clearKarlAskQueueState,
  readKarlAskQueueState,
  writeKarlAskQueueState,
} from "./karlAskQueueStorage";
import { emptyKarlAskQueueState } from "./types";
import type { KarlAsk, KarlAnswer, KarlAskTool } from "../karlAsks";
import type {
  KarlAskEffect,
  KarlAskQueueEvent,
  KarlAskQueueState,
  KarlPlanSnapshot,
  UseKarlAskQueue,
} from "./types";

export const useKarlAskQueue: UseKarlAskQueue = ({
  steps,
  sourceEdit,
  group,
  projectId,
  planName,
  t,
}) => {
  const stateRef = useRef<KarlAskQueueState>(emptyKarlAskQueueState);
  const asksRef = useRef<KarlAsk[]>([]);
  const snapshotRef = useRef<KarlPlanSnapshot>(planSnapshot({ sourceEdit }));
  const loadedFor = useRef<string | null>(null);

  const [state, setState] = useState<KarlAskQueueState>(() =>
    readKarlAskQueueState({ projectId, planName, group }),
  );
  stateRef.current = state;

  // Every board he has accepted, read back out of what he answered. Held as one
  // list rather than a second piece of state that could disagree with it.
  const confirmed = useMemo(
    () =>
      state.history
        .filter((record) => record.outcome !== "declined")
        .map((record) => record.confirmKey)
        .filter((key): key is string => Boolean(key)),
    [state.history],
  );

  // One array, memoised on the history, shared by the ask builder and by every
  // surface outside the queue. Its identity is load-bearing: consumers key
  // effects on it.
  const settled = useMemo(
    () =>
      state.history.map((record) => ({
        askId: record.askId,
        outcome: record.outcome,
      })),
    [state.history],
  );

  const zones = sourceEdit?.zones;
  const zoneCountByLabel = useMemo(() => {
    const counts: Record<string, number> = {};
    (zones ?? []).forEach((zone) => {
      counts[zone.sourceLabel] = (counts[zone.sourceLabel] ?? 0) + 1;
    });
    return counts;
  }, [zones]);

  const { asks: allAsks, notes } = useMemo(
    () =>
      buildKarlAsks({
        steps,
        added: sourceEdit?.added,
        removed: sourceEdit?.removed,
        placed: sourceEdit?.placed,
        confirmed,
        settled,
        removedDevices: sourceEdit?.removedDeviceMarkers,
        removalGroups: sourceEdit?.removalGroups,
        zoneCountByLabel,
        sourceNames: sourceEdit?.names,
        mainBoard: sourceEdit?.mainBoard,
        feeds: sourceEdit?.boardFeeds,
        heights: sourceEdit?.heights,
        feeders: sourceEdit?.feeders,
        planBoards: sourceEdit?.planBoards,
        t,
      }),
    [
      sourceEdit?.planBoards,
      sourceEdit?.heights,
      sourceEdit?.feeders,
      sourceEdit?.mainBoard,
      sourceEdit?.boardFeeds,
      zoneCountByLabel,
      sourceEdit?.names,
      steps,
      sourceEdit?.added,
      sourceEdit?.removed,
      sourceEdit?.placed,
      confirmed,
      settled,
      sourceEdit?.removedDeviceMarkers,
      sourceEdit?.removalGroups,
      t,
    ],
  );

  const asks = useMemo(
    () => allAsks.filter((ask) => ask.group === group),
    [allAsks, group],
  );
  asksRef.current = asks;

  const runEffects = useCallback(
    (effects: KarlAskEffect[]): void => {
      for (const effect of effects) {
        switch (effect.kind) {
          case "arm":
            if (effect.arm.kind === "moveSource") {
              sourceEdit?.start(effect.arm.label);
            } else if (effect.arm.kind === "placeSource") {
              sourceEdit?.add(effect.arm.name);
            } else if (effect.arm.kind === "drawZone") {
              sourceEdit?.drawZone(effect.arm.label);
            } else if (effect.arm.kind === "pickMainBoard") {
              sourceEdit?.pickMainBoard(effect.arm.placesAs);
            } else if (effect.arm.kind === "pickFeederFor") {
              sourceEdit?.pickFeederFor(effect.arm.category);
            } else if (effect.arm.kind === "pickUpstreamFor") {
              sourceEdit?.feeders?.pickUpstreamFor(effect.arm.toLabel);
            } else if (effect.arm.kind === "pickRoute") {
              // Answered from the circuit list, not the plan — nothing to arm
              // here beyond the panel already knowing it is waiting.
            } else {
              // A selection left over from the last pick makes the next one
              // impossible to detect: it can never go from nothing to something.
              sourceEdit?.clearDeviceSelection();
              sourceEdit?.selectDevices(
                effect.arm.kind === "boxRemove" ? "remove" : "add",
                effect.arm.kind === "drawBox"
                  ? effect.arm.circuitId
                  : effect.arm.circuitId,
              );
            }
            break;
          case "assignSelection":
            sourceEdit?.assignSelectionTo(effect.circuitId);
            break;
          case "removeSelection":
            // One box, one group: struck one at a time they became N unrelated
            // entries he could not tell apart afterwards.
            sourceEdit?.removeDevices(
              (sourceEdit.deviceSelection?.devices ?? []).map(
                (device) => device.destinationId,
              ),
            );
            // The catch is spent: leaving it published would make the next box
            // look like it had already landed.
            sourceEdit?.clearDeviceSelection();
            break;
          case "assignRemovalGroup": {
            // The run he just isolated IS the answer.
            const target = sourceEdit?.selectedCircuit;
            if (target) sourceEdit?.assignGroupTo(effect.groupId, target);
            break;
          }
          case "restoreRemovalGroup":
            sourceEdit?.restoreGroup(effect.groupId);
            break;
          case "parkDevices":
            sourceEdit?.parkDevices(effect.groupId, effect.destinationIds);
            break;
          case "editRouteShape":
            sourceEdit?.editRouteShape(effect.circuitId);
            break;
          case "closeRemovalGroup":
            sourceEdit?.closeRemovalGroup(effect.groupId);
            break;
          case "cancelArm":
            sourceEdit?.cancel();
            break;
          case "removeSource":
            sourceEdit?.remove(effect.label);
            break;
          case "removeSourceTo":
            sourceEdit?.removeTo(effect.label, effect.toLabel);
            break;
          case "restoreSource":
            sourceEdit?.restore(effect.label);
            break;
          case "setMainBoard":
            sourceEdit?.setMainBoard(effect.label);
            break;
          case "clearMainBoard":
            sourceEdit?.clearMainBoard();
            break;
          case "setBoardRole":
            sourceEdit?.setBoardRole(effect.label, effect.role);
            break;
          case "clearBoardRole":
            sourceEdit?.clearBoardRole(effect.label);
            break;
          case "setBoardFeeds":
            sourceEdit?.setBoardFeeds(effect.label, effect.feed);
            break;
          case "clearBoardFeeds":
            sourceEdit?.clearBoardFeeds(effect.label);
            break;
          case "unbillCategory":
            sourceEdit?.setCategoryUnbilled(effect.category, true);
            break;
          case "billCategory":
            sourceEdit?.setCategoryUnbilled(effect.category, false);
            break;
          case "setHeight":
            sourceEdit?.heights.set(effect.target, effect.heightM);
            break;
          case "clearHeight":
            sourceEdit?.heights.set(effect.target, null);
            break;
          case "setFeederFrom":
            sourceEdit?.feeders?.setFrom(effect.toLabel, effect.fromLabel);
            break;
          case "clearFeederFrom":
            sourceEdit?.feeders?.clearFrom(effect.toLabel);
            break;
          case "setFeederOffFloor":
            sourceEdit?.feeders?.setOffFloor(effect.toLabel, true);
            break;
          case "feederOnFloor":
            sourceEdit?.feeders?.setOffFloor(effect.toLabel, false);
            break;
          case "setFeederType":
            sourceEdit?.feeders?.setCableType(effect.toLabel, effect.cableType);
            break;
          case "clearFeederType":
            sourceEdit?.feeders?.setCableType(effect.toLabel, null);
            break;
          // `confirm`/`unconfirm`/`renameSource` need no plan interaction:
          // confirmation is read back out of the history above.
          default:
            break;
        }
      }
    },
    [sourceEdit],
  );

  const dispatch = useCallback(
    (event: KarlAskQueueEvent): void => {
      const result = karlAskQueueReducer({
        state: stateRef.current,
        event,
        asks: asksRef.current,
      });
      if (result.state !== stateRef.current) {
        stateRef.current = result.state;
        setState(result.state);
      }
      // Outside the state updater on purpose: React may invoke an updater twice,
      // and arming the plan twice leaves a grab nobody asked for.
      runEffects(result.effects);
    },
    [runEffects],
  );

  const answer = useCallback(
    (ask: KarlAsk, chosen: KarlAnswer): void => {
      const viewBeforeAnswer = readAskQueue({
        asks: asksRef.current,
        state: stateRef.current,
      });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_QUESTION_ANSWERED, {
        // Ask ids are "source:<kind>:<board>"; the board part is not sent
        askKind: ask.id.split(":")[1] ?? ask.id,
        answerId: chosen.id,
        outcome: askAnswerEffect({ ask, answer: chosen }).outcome,
        position: viewBeforeAnswer.position,
        total: viewBeforeAnswer.total,
        isRevisit: viewBeforeAnswer.mode === "revisiting",
      });
      dispatch({ type: "answer", ask, answer: chosen });
    },
    [dispatch],
  );

  // Memoised on `dispatch` like `answer`: a fresh function every render restarts
  // the card's effects, which is how an unmemoised `settled` once looped here.
  const runTool = useCallback(
    (ask: KarlAsk, tool: KarlAskTool): void => {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_TOOL_USED, {
        toolId: tool.id,
      });
      dispatch({ type: "tool", ask, tool });
    },
    [dispatch],
  );

  // Start this group over. Clears the store BEFORE the state so the reload
  // effect cannot read the old answers straight back in.
  const reset = useCallback(() => {
    clearKarlAskQueueState({ projectId, planName, group });
    stateRef.current = emptyKarlAskQueueState;
    setState(emptyKarlAskQueueState);
  }, [projectId, planName, group]);

  const back = useCallback(() => dispatch({ type: "back" }), [dispatch]);
  const forward = useCallback(() => dispatch({ type: "forward" }), [dispatch]);
  const walkAgain = useCallback(
    () => dispatch({ type: "rewindAll" }),
    [dispatch],
  );
  const cancelArm = useCallback(() => dispatch({ type: "cancel" }), [dispatch]);

  // Answered or still ahead of him, the ask has to be one this group can put on
  // screen. An ask only history remembers would render the finished card, which
  // is a worse answer than letting the caller do something else.
  const jumpTo = useCallback(
    (askId: string): boolean => {
      if (!asksRef.current.some((ask) => ask.id === askId)) return false;
      dispatch({ type: "jumpTo", askId });
      return true;
    },
    [dispatch],
  );

  const view = useMemo(() => readAskQueue({ asks, state }), [asks, state]);

  useEffect(() => {
    const key = `${projectId ?? ""}.${planName ?? ""}.${group}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    const loaded = readKarlAskQueueState({ projectId, planName, group });
    stateRef.current = loaded;
    setState(loaded);
  }, [projectId, planName, group]);

  useEffect(() => {
    writeKarlAskQueueState({ projectId, planName, group, state });
  }, [projectId, planName, group, state]);

  // An ask the rebuild no longer carries cannot be waited on or deferred. Its
  // history record stays: he did answer it, and the answer is why it is gone.
  useEffect(() => {
    const present = new Set(asks.map((ask) => ask.id));
    const gone = stateRef.current.deferred.filter((id) => !present.has(id));
    const armed = stateRef.current.armed;
    const armedGone = armed !== null && !present.has(armed.askId);
    if (!gone.length && !armedGone) return;
    dispatch({
      type: "vanished",
      askIds: armedGone ? [...gone, armed.askId] : gone,
    });
  }, [asks, dispatch]);

  // No dependency list: the plan moves through props this hook does not own, and
  // the snapshot comparison is the guard that keeps this cheap.
  useEffect(() => {
    const after = planSnapshot({ sourceEdit });
    const before = snapshotRef.current;
    if (isSamePlanSnapshot(before, after)) return;
    snapshotRef.current = after;
    const armed = stateRef.current.armed;
    if (!armed) return;
    const settlement = askArmSettled({ arm: armed.arm, before, after });
    if (settlement === "committed") dispatch({ type: "commit" });
    else if (settlement === "cancelled") dispatch({ type: "cancel" });
  });

  return {
    view,
    notes: group === "sources" ? [] : notes,
    answer,
    back,
    forward,
    walkAgain,
    jumpTo,
    cancelArm,
    runTool,
    reset,
    isFinished: view.mode === "finished",
    // The memo above, not a fresh map: a new array every render made the panel's
    // route-status effect fire every render, which set state in the page, which
    // re-rendered the panel — an unbounded loop.
    settled,
  };
};
